import type { ProgrammeState, RepwiseEvent } from '@repwise/domain';
import type { SyncStatus } from '@repwise/sync';
import { isStandalone } from './hooks.ts';

/**
 * In-app feedback (ported from Lily309). Saved to Supabase via submit_feedback(); triaged by hand into
 * docs/06-feedback-backlog.md. Context is shown to the user before sending and never includes their email.
 */
export type FeedbackKind = 'bug' | 'idea' | 'confusing' | 'praise' | 'other';
export const FEEDBACK_KINDS: { kind: FeedbackKind; label: string }[] = [
  { kind: 'bug', label: 'Something broke' },
  { kind: 'confusing', label: 'Confusing' },
  { kind: 'idea', label: 'Idea' },
  { kind: 'praise', label: 'I like this' },
  { kind: 'other', label: 'Other' },
];
export const DRAFT_KEY = 'repwise.feedbackDraft';

declare const __APP_BUILD__: string;
export const APP_BUILD = typeof __APP_BUILD__ === 'string' ? __APP_BUILD__ : 'dev';

/** The last few uncaught errors on this device: the context that makes a "something broke" report actionable. */
const recent: { at: number; message: string }[] = [];
export function captureErrors(target: Window = window): void {
  const push = (message: string) => {
    recent.push({ at: Date.now(), message: message.slice(0, 300) });
    if (recent.length > 5) recent.shift();
  };
  target.addEventListener('error', (e) => push(e.message || String(e.error)));
  target.addEventListener('unhandledrejection', (e) => push(`unhandled: ${e.reason instanceof Error ? e.reason.message : String(e.reason)}`));
}

export function feedbackContext(o: { route: string; status: SyncStatus | null; programme: ProgrammeState | null; events: RepwiseEvent[] | null; includeWorkout: boolean }): Record<string, unknown> {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const ctx: Record<string, unknown> = {
    app: { build: APP_BUILD, route: o.route },
    device: {
      userAgent: nav?.userAgent.slice(0, 300),
      installed: isStandalone(),
      online: nav?.onLine,
      screen: typeof window === 'undefined' ? undefined : `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio}`,
      language: nav?.language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      theme: (() => {
        try {
          return localStorage.getItem('repwise.theme') ?? 'dark';
        } catch {
          return undefined;
        }
      })(),
    },
    sync: o.status ? { label: o.status.label, pending: o.status.pending, deadLetters: o.status.deadLetters, lastSyncAt: o.status.lastSyncAt, oldestPendingAt: o.status.oldestPendingAt } : null,
    recentErrors: [...recent],
  };
  if (o.includeWorkout && o.programme) {
    const p = o.programme;
    ctx.workout = {
      unit: p.settings.unit,
      nextKind: p.nextKind,
      completed: p.completed,
      inProgress: p.inProgress.length,
      lastCompletedAt: p.lastCompletedAt,
      next: Object.fromEntries(Object.entries(p.lifts).map(([l, x]) => [l, { weight: x.state.weight, misses: x.state.misses, reason: x.reason }])),
      accessories: { curl: p.settings.curl.enabled, pushup: p.settings.pushup.enabled },
      events: o.events?.length,
    };
  }
  return ctx;
}
